import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Check, Eye, Plus, Sparkles } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { instructorApi } from "../../../services/instructorApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";
import {
  formatScheduleCompact,
  pricingModelLabel,
} from "../../../lib/scheduleDisplay";

const inputClass =
  "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DAY_FULL = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

const WEEKEND = new Set(["Sat", "Sun"]);

const LEVELS = ["Beginner", "Intermediate", "Advanced", "All Levels"];

const DURATIONS = ["30 min", "45 min", "60 min", "75 min", "90 min", "120 min"];

const CURRENCIES = ["INR", "USD", "EUR", "GBP"];

const CATEGORIES = [
  "Hatha",
  "Vinyasa",
  "Ashtanga",
  "Yin",
  "Restorative",
  "Prenatal",
  "Meditation",
  "Pranayama",
  "Kundalini",
  "Power Yoga",
  "Yoga Therapy",
  "Yoga Nidra",
  "Senior Yoga",
  "Private Yoga",
  "Workshop",
];

/** How the class is delivered */
const CLASS_FORMATS = [
  "Live online group",
  "Live online 1:1",
  "Workshop",
];

/** How students pay (distinct from how often the class meets) */
const PRICING_MODELS = [
  { value: "drop_in", label: "Pay per class" },
  { value: "pack", label: "Class pack (multi-class)" },
  { value: "monthly", label: "Monthly membership (recurring)" },
];

const TIMEZONES = [
  "IST",
  "UTC",
  "GMT",
  "EST",
  "CST",
  "MST",
  "PST",
  "CET",
  "EET",
  "GST",
  "SGT",
  "JST",
  "AEST",
];

/**
 * Standard templates covering common instructor offerings.
 * Clicking one fills the form so the instructor can adjust before saving.
 */
const TEMPLATES = [
  {
    id: "morning-group",
    label: "Morning group class",
    title: "Morning Yoga Flow",
    category: "Vinyasa",
    format: "Live online group",
    duration: "60 min",
    level: "All Levels",
    listing_type: "Online Course",
    pricing_model: "drop_in",
    price: "500",
    currency: "INR",
    schedule_days: ["Mon", "Wed", "Fri"],
    schedule_time: "07:00",
  },
  {
    id: "evening-group",
    label: "Evening group class",
    title: "Evening Restorative Yoga",
    category: "Restorative",
    format: "Live online group",
    duration: "60 min",
    level: "All Levels",
    listing_type: "Online Course",
    pricing_model: "drop_in",
    price: "500",
    currency: "INR",
    schedule_days: ["Tue", "Thu"],
    schedule_time: "18:30",
  },
  {
    id: "monthly-unlimited",
    label: "Monthly subscription",
    title: "Unlimited Monthly Yoga Membership",
    category: "Hatha",
    format: "Live online group",
    duration: "60 min",
    level: "All Levels",
    listing_type: "Online Course",
    pricing_model: "monthly",
    price: "2999",
    currency: "INR",
    schedule_days: ["Mon", "Wed", "Fri"],
    schedule_time: "07:00",
  },
  {
    id: "private-1on1",
    label: "Private 1:1",
    title: "Private 1:1 Yoga Session",
    category: "Private Yoga",
    format: "Live online 1:1",
    duration: "60 min",
    level: "All Levels",
    listing_type: "Online Course",
    pricing_model: "drop_in",
    price: "1500",
    currency: "INR",
    schedule_days: ["Mon"],
    schedule_time: "09:00",
  },
  {
    id: "prenatal",
    label: "Prenatal class",
    title: "Prenatal Gentle Flow",
    category: "Prenatal",
    format: "Live online group",
    duration: "45 min",
    level: "Beginner",
    listing_type: "Prenatal Yoga",
    pricing_model: "drop_in",
    price: "600",
    currency: "INR",
    schedule_days: ["Tue", "Thu"],
    schedule_time: "10:00",
  },
  {
    id: "prenatal-monthly",
    label: "Prenatal monthly",
    title: "Prenatal Monthly Membership",
    category: "Prenatal",
    format: "Live online group",
    duration: "45 min",
    level: "Beginner",
    listing_type: "Prenatal Yoga",
    pricing_model: "monthly",
    price: "2499",
    currency: "INR",
    schedule_days: ["Tue", "Thu", "Sat"],
    schedule_time: "10:00",
  },
  {
    id: "meditation",
    label: "Meditation",
    title: "Guided Meditation",
    category: "Meditation",
    format: "Live online group",
    duration: "30 min",
    level: "All Levels",
    listing_type: "Online Course",
    pricing_model: "drop_in",
    price: "300",
    currency: "INR",
    schedule_days: ["Daily"],
    schedule_time: "20:30",
  },
  {
    id: "class-pack",
    label: "8-class pack",
    title: "8-Class Yoga Pack",
    category: "Hatha",
    format: "Live online group",
    duration: "60 min",
    level: "All Levels",
    listing_type: "Online Course",
    pricing_model: "pack",
    price: "3500",
    currency: "INR",
    schedule_days: ["Mon", "Wed", "Fri"],
    schedule_time: "07:00",
  },
  {
    id: "weekend-workshop",
    label: "Weekend workshop",
    title: "Weekend Yoga Workshop",
    category: "Workshop",
    format: "Workshop",
    duration: "120 min",
    level: "Intermediate",
    listing_type: "Online Course",
    pricing_model: "drop_in",
    price: "1200",
    currency: "INR",
    schedule_days: ["Sat"],
    schedule_time: "10:00",
  },
  {
    id: "therapy",
    label: "Yoga therapy",
    title: "Therapeutic Yoga Session",
    category: "Yoga Therapy",
    format: "Live online 1:1",
    duration: "60 min",
    level: "All Levels",
    listing_type: "Online Course",
    pricing_model: "drop_in",
    price: "1800",
    currency: "INR",
    schedule_days: ["Wed"],
    schedule_time: "11:00",
  },
];

const emptyForm = {
  title: "",
  category: "Hatha",
  format: "Live online group",
  duration: "60 min",
  level: "All Levels",
  listing_type: "Online Course",
  pricing_model: "drop_in",
  price: "",
  currency: "INR",
  schedule_days: ["Mon", "Wed"],
  schedule_time: "07:00",
  timezone: "IST",
};

function pricingLabel(model) {
  if (model === "monthly") return "per month";
  if (model === "pack") return "per pack";
  return "per class";
}

function resolvedScheduleDays(days) {
  if (!Array.isArray(days) || days.length === 0) return [];
  if (days.includes("Daily")) return [...DAYS];
  return days;
}

function templateHint(t) {
  return formatScheduleCompact([
    {
      days: resolvedScheduleDays(t.schedule_days),
      time: t.schedule_time,
      timezone: "IST",
      pricing_model: t.pricing_model,
    },
  ]);
}

function formatPriceDisplay(o) {
  if (o.price == null) return "No price";
  const model = o.schedule_json?.[0]?.pricing_model;
  const suffix =
    model === "monthly" ? "/mo" : model === "pack" ? " pack" : "/class";
  return `${o.currency} ${o.price}${suffix}`;
}

function offeringScheduleLine(o) {
  return (
    formatScheduleCompact(o.schedule_json, o.schedule_summary) || "No schedule"
  );
}

export default function OfferingsStep() {
  const { instructor, refreshInstructor } = useAuth();
  const navigate = useNavigate();
  const [offerings, setOfferings] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOfferings(instructor?.offerings || []);
    const tz = instructor?.profile?.timezone;
    if (tz) {
      setForm((f) => ({ ...f, timezone: tz }));
    }
  }, [instructor]);

  function applyTemplate(t) {
    setError("");
    setForm({
      title: t.title,
      category: t.category,
      format: t.format,
      duration: t.duration,
      level: t.level,
      listing_type: t.listing_type,
      pricing_model: t.pricing_model,
      price: t.price,
      currency: t.currency,
      schedule_days: t.schedule_days,
      schedule_time: t.schedule_time,
      timezone: instructor?.profile?.timezone || "IST",
    });
  }

  function toggleDay(day) {
    setForm((f) => {
      const has = f.schedule_days.includes(day);
      let next;
      if (day === "Daily") {
        next = has ? [] : ["Daily"];
      } else {
        next = has
          ? f.schedule_days.filter((d) => d !== day && d !== "Daily")
          : [...f.schedule_days.filter((d) => d !== "Daily"), day];
      }
      return { ...f, schedule_days: next };
    });
  }

  async function addOffering(payload) {
    setError("");
    setSaving(true);
    try {
      await instructorApi.createOffering(payload);
      setForm({
        ...emptyForm,
        timezone: instructor?.profile?.timezone || "IST",
      });
      await refreshInstructor();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.schedule_days.length) {
      setError("Select at least one schedule day.");
      return;
    }
    const days = form.schedule_days.includes("Daily")
      ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
      : form.schedule_days;

    const pricingText = pricingModelLabel(form.pricing_model) || "Per class";
    const formatWithPricing = `${form.format} · ${pricingText}`;

    await addOffering({
      title: form.title,
      category: form.category,
      format: formatWithPricing,
      duration: form.duration,
      level: form.level,
      listing_type: form.listing_type,
      price: form.price === "" ? null : Number(form.price),
      currency: form.currency,
      schedule_json: [
        {
          days,
          time: form.schedule_time,
          timezone: form.timezone || "IST",
          pricing_model: form.pricing_model,
          label: pricingText,
        },
      ],
    });
  }

  async function removeOffering(id) {
    try {
      await instructorApi.deleteOffering(id);
      await refreshInstructor();
    } catch (err) {
      setError(err.message);
    }
  }

  function offeringAccent(category) {
    const key = String(category || "").toLowerCase();
    if (/(meditat|nidra|mindfulness|sound)/.test(key)) {
      return {
        panel: "border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50/80",
        badge: "bg-teal-600 text-white",
        schedule: "text-teal-800",
      };
    }
    if (/(restor|yin|prenatal|postnatal|senior|chair)/.test(key)) {
      return {
        panel: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/70",
        badge: "bg-amber-500 text-white",
        schedule: "text-amber-900",
      };
    }
    if (/(therapy|therapeutic|ayurveda|wellness)/.test(key)) {
      return {
        panel: "border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50/70",
        badge: "bg-violet-600 text-white",
        schedule: "text-violet-900",
      };
    }
    if (/(power|ashtanga|hot|bikram|aerial|acro)/.test(key)) {
      return {
        panel: "border-rose-200 bg-gradient-to-br from-rose-50 to-orange-50/60",
        badge: "bg-rose-600 text-white",
        schedule: "text-rose-900",
      };
    }
    return {
      panel: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50/80",
      badge: "bg-emerald-600 text-white",
      schedule: "text-emerald-900",
    };
  }

  return (
    <OnboardingStepFrame
      title="Classes students can book"
      subtitle="Add at least one class or course with price and schedule. Use a template to start, then adjust."
    >
      {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      <div className="mb-4">
        <p className="text-sm font-medium text-gray-700 mb-2 inline-flex items-center gap-1.5">
          <Sparkles size={15} className="text-amber-500" /> Quick-start templates
        </p>
        <p className="text-xs text-gray-500 mb-3">
          Each template shows which days the class meets and how students pay.
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={saving}
              onClick={() => applyTemplate(t)}
              className="flex flex-col items-start gap-0.5 text-left text-xs px-3 py-2.5 rounded-xl border border-emerald-200 bg-white text-emerald-900 font-medium hover:bg-emerald-50 hover:border-emerald-300 transition disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-1.5">
                <Plus size={13} className="text-emerald-500 shrink-0" />
                {t.label}
              </span>
              <span className="pl-5 text-[11px] font-normal text-emerald-800/75 leading-snug">
                {templateHint(t)}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 mb-6">
        {offerings.map((o) => {
          const accent = offeringAccent(o.category);
          return (
            <div
              key={o.id}
              className={`rounded-xl border p-4 flex justify-between gap-3 shadow-sm ${accent.panel}`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="font-semibold text-gray-900">{o.title}</p>
                  {o.category ? (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${accent.badge}`}
                    >
                      {o.category}
                    </span>
                  ) : null}
                </div>
                <p className="text-sm text-gray-600">
                  {o.listing_type} · {o.format} · {formatPriceDisplay(o)}
                </p>
                <p className={`text-sm font-medium mt-1 ${accent.schedule}`}>
                  {offeringScheduleLine(o)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeOffering(o.id)}
                className="shrink-0 self-start rounded-lg border border-red-200 bg-white/80 px-2.5 py-1 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-gray-100 rounded-2xl p-6 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Title</span>
          <input
            className={inputClass}
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Morning Yoga Flow"
          />
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Style / category</span>
            <select
              className={inputClass}
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Listing type</span>
            <select
              className={inputClass}
              value={form.listing_type}
              onChange={(e) => setForm({ ...form, listing_type: e.target.value })}
            >
              <option value="Online Course">Online Course</option>
              <option value="Prenatal Yoga">Prenatal Yoga</option>
            </select>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Class format</span>
            <select
              className={inputClass}
              value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value })}
            >
              {CLASS_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">How students pay</span>
            <select
              className={inputClass}
              value={form.pricing_model}
              onChange={(e) => setForm({ ...form, pricing_model: e.target.value })}
            >
              {PRICING_MODELS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-gray-500">
              This is the payment model, not how often the class meets.
            </span>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Level</span>
            <select
              className={inputClass}
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Duration</span>
            <select
              className={inputClass}
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
            >
              {DURATIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              Price ({pricingLabel(form.pricing_model)})
            </span>
            <input
              type="number"
              min="0"
              step="1"
              className={inputClass}
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Currency</span>
            <select
              className={inputClass}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays size={16} className="text-emerald-600" />
            <span className="text-sm font-semibold text-gray-800">Which days does this class meet?</span>
          </div>

          {(() => {
            const dailyActive = form.schedule_days.includes("Daily");
            return (
              <>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {DAYS.map((day) => {
                    const active = dailyActive || form.schedule_days.includes(day);
                    const weekend = WEEKEND.has(day);
                    const activeClasses = weekend
                      ? "bg-amber-500 border-amber-500 text-white shadow-sm"
                      : "bg-emerald-600 border-emerald-600 text-white shadow-sm";
                    const idleClasses = weekend
                      ? "bg-white border-amber-200 text-amber-700 hover:bg-amber-50"
                      : "bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50";
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        aria-pressed={active}
                        title={DAY_FULL[day]}
                        className={`relative flex flex-col items-center justify-center rounded-xl border py-2.5 text-xs font-semibold transition ${
                          active ? activeClasses : idleClasses
                        }`}
                      >
                        {active && (
                          <Check size={12} className="absolute top-1 right-1 opacity-90" />
                        )}
                        <span>{day}</span>
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => toggleDay("Daily")}
                  aria-pressed={dailyActive}
                  className={`mt-2 w-full rounded-xl border py-2.5 text-sm font-semibold transition ${
                    dailyActive
                      ? "bg-gradient-to-r from-emerald-600 to-teal-500 border-transparent text-white shadow-sm"
                      : "bg-white border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  }`}
                >
                  {dailyActive ? "Every day selected" : "Select daily (every day)"}
                </button>

                <p className="mt-2 text-xs text-gray-500">
                  {dailyActive
                    ? "Runs every day of the week."
                    : form.schedule_days.length
                      ? `Selected: ${form.schedule_days
                          .map((d) => DAY_FULL[d] || d)
                          .join(", ")}`
                      : "Tap the days this class runs. Weekends are shown in amber."}
                </p>
              </>
            );
          })()}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Time</span>
            <input
              type="time"
              className={inputClass}
              value={form.schedule_time}
              onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Timezone</span>
            <select
              className={inputClass}
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="rounded-xl border border-teal-100 bg-teal-50/50 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-800 inline-flex items-center gap-1.5">
            <Eye size={13} /> Students will see
          </p>
          <p className="mt-1.5 text-sm font-medium text-gray-900">
            {formatScheduleCompact([
              {
                days: resolvedScheduleDays(form.schedule_days),
                time: form.schedule_time,
                timezone: form.timezone || "IST",
                pricing_model: form.pricing_model,
              },
            ]) || "Select days, time, and pricing to preview."}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Cadence (which days) and payment model are shown separately so bookings stay clear.
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-500 text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:from-emerald-700 hover:to-teal-600 disabled:opacity-60"
        >
          <Plus size={16} />
          {saving ? "Saving…" : "Add offering"}
        </button>
      </form>

      <div className="flex justify-between mt-6">
        <Link to="/instructor/onboarding/media" className="text-sm text-gray-600 hover:underline">
          Back
        </Link>
        <button
          type="button"
          onClick={() => navigate("/instructor/onboarding/review")}
          className="rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700"
        >
          Continue to review
        </button>
      </div>
    </OnboardingStepFrame>
  );
}
